<script lang="ts">
  import type { BriefBuilderAnswers } from './types';
  import { sidebarValue } from './types';
  import { buildSteps } from './questions';

  export let answers: BriefBuilderAnswers;
  export let currentStepId: string | null = null;
  export let liveDraft: string = '';
  export let showTimer: boolean = false;
  export let elapsedSeconds: number = 0;
  export let targetSeconds: number = 600;

  $: steps = buildSteps(answers);
  $: visible = steps.filter((s) => {
    const val = sidebarValue(
      answers,
      s.id,
      currentStepId === s.id ? liveDraft : undefined
    );
    return (
      val ||
      answers.unknowns.includes(s.id) ||
      currentStepId === s.id
    );
  });

  function formatTime(total: number): string {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  $: overTarget = elapsedSeconds > targetSeconds;
</script>

<aside class="bb-sidebar" aria-label="Your brief assembling">
  {#if showTimer}
    <div class="bb-timer" class:over={overTarget} aria-live="polite">
      {formatTime(elapsedSeconds)} elapsed · target &lt; {formatTime(targetSeconds)}
    </div>
  {/if}

  <h2 class="bb-sidebar-title">Your brief, assembling</h2>

  {#if visible.length === 0}
    <p class="bb-sidebar-empty">Your answers will appear here as you progress.</p>
  {:else}
    <ol class="bb-sidebar-list">
      {#each visible as step (step.id)}
        <li class="bb-sidebar-item" class:current={step.id === currentStepId}>
          <div class="bb-sidebar-label">
            {step.number} · {step.label}
          </div>
          <p class="bb-sidebar-body">
            {sidebarValue(
              answers,
              step.id,
              currentStepId === step.id ? liveDraft : undefined
            ) || '…'}
          </p>
        </li>
      {/each}
    </ol>
  {/if}
</aside>

<style>
  .bb-sidebar {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: var(--space-4);
    height: 100%;
    min-height: 0;
    overflow: auto;
    padding: var(--space-6) var(--space-5);
    background: transparent;
    text-align: left;
  }

  .bb-timer {
    width: 100%;
    font-size: var(--text-xs);
    font-weight: 500;
    color: var(--accent-orange-ink);
    font-variant-numeric: tabular-nums;
    text-align: left;
  }

  .bb-timer.over {
    color: var(--status-danger-ink);
  }

  .bb-sidebar-title {
    margin: 0;
    width: 100%;
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    text-align: left;
  }

  .bb-sidebar-empty {
    margin: 0;
    width: 100%;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
    line-height: 1.5;
    text-align: left;
  }

  .bb-sidebar-list {
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-4);
    text-align: left;
  }

  .bb-sidebar-item {
    width: 100%;
    padding-bottom: var(--space-4);
    border-bottom: 1px solid var(--border-color);
    text-align: left;
  }

  .bb-sidebar-item.current {
    padding-left: var(--space-3);
    margin-left: calc(-1 * var(--space-3));
    border-left: 3px solid var(--primary);
  }

  .bb-sidebar-item:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .bb-sidebar-label {
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    margin-bottom: var(--space-1);
    text-align: left;
  }

  .bb-sidebar-body {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--grey);
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
  }
</style>
